<template>
  <div v-if="user">
    <h1>Account details</h1>
    <!-- The user has not selected favourite co-workers or has not toggled the no-preferred-co-workers switch -->
    <div v-if="authStore.user.favourite_co_workers.length === 0 && !authStore.user.no_preferred_co_workers ">
      <div class="alert alert-danger" role="alert">
        <div class="row">
          <div class="col-auto">
            <i class="fa-solid fa-hand-point-right"></i>
          </div>
          <div class="col">
            <p>Specify your favourite co-workers, or ... turn on the "No preferred co-workers" switch if you appreciate
              them all!</p>
          </div>
        </div>
      </div>
    </div>
    <!-- The user has not selected a role for an organisation -->
    <div v-if="organisationsWithoutSelectedRole.length > 0">
      <div class="alert alert-danger" role="alert">
        <div class="row">
          <div class="col-auto"><i class="fa-solid fa-hand-point-right"></i></div>
          <div class="col">
            <p v-for="organisation in organisationsWithoutSelectedRole" :key="organisation.id">
              Choose at least one role to get started with {{ organisation.name }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <dl class="row">
      <dt class="col-sm-3">Username</dt>
      <dd class="col-sm-9">{{ user.username }}</dd>
      <dt class="col-sm-3">First name</dt>
      <dd class="col-sm-9">{{ user.first_name }}</dd>
      <dt class="col-sm-3">Last name</dt>
      <dd class="col-sm-9">{{ user.last_name }}</dd>
      <dt class="col-sm-3">Email address</dt>
      <dd class="col-sm-9">{{ user.email }}</dd>
      <dt class="col-sm-3">Mobile number</dt>
      <dd class="col-sm-9">{{ formattedMobileNumber }}</dd>
      <dt class="col-sm-3">Member of</dt>
      <dd class="col-sm-9">{{ organisationNames }}</dd>
      <dt v-if="organisationAdminNames.length !== 0" class="col-sm-3">Administrator for</dt>
      <dd v-if="organisationAdminNames.length !== 0" class="col-sm-9">{{ organisationAdminNames }}</dd>

      <dt class="col-sm-3">Favourite co-workers</dt>
      <dd :class="{'text-muted fst-italic': user.no_preferred_co_workers }" class="col-sm-9">{{
          user.favourite_co_workers
        }}
      </dd>

      <dt class="col-sm-3">No preferred co-workers</dt>
      <dd class="col-sm-9">
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" v-model="user.no_preferred_co_workers"
                 @change="toggleNoPreferredCoWorkers">
        </div>
      </dd>
    </dl>
    <div class="d-grid gap-2 col-12 mb-3">
      <button @click="showEditModal = true" class="btn btn-primary">Edit account details</button>
      <button @click="showChangePasswordModal = true" class="btn btn-secondary">Change password</button>
      <!--    <button @click="toggleChangePasswordModal" class="btn btn-secondary">Change password</button>-->
    </div>
    <EditDetailsModal v-if="showEditModal" :user="user" @close="showEditModal = false" @save-details="saveDetails"/>
    <ChangePasswordModal v-if="showChangePasswordModal" :user="user" @close-password="showChangePasswordModal = false"
                         @save-password="savePassword"/>
    <div v-for="organisation in organisations" :key="organisation.slug">
      <!-- Iterate over each organisation and display the roles -->
      <div v-if="organisation.roles && organisation.roles.length !== 0">
        <h1>Roles in {{ organisation.name }}</h1>
        <div v-for="role in organisation.roles" :key="role.id">
          <!-- Show the roles for the organisation -->
          <p v-if="(isRoleLinkedToUser(role.id)) !== null">
            <!-- Show the role that the user is linked to -->
            <button type="button" class="btn btn-success btn-sm"
                    @click="() => handleDeleteUserRole(isRoleLinkedToUser(role.id),  organisation)"><i
                class="fa-regular fa-square-check"/></button>
            {{ role.description }}
          </p>
          <p v-else>
            <!-- Show the roles that the user is not linked to -->
            <button type="button" class="btn btn-secondary btn-sm me-1"
                    @click="() => handleCreateUserRole(role.id,organisation)"><i class="fa-regular fa-square"/></button>
            {{ role.description }}
          </p>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <p>Loading user data...</p>
  </div>
</template>

<script>
// import {getCSRFToken, useAuthStore} from '@/store/auth.js'
import {useAuthStore} from '@/store/auth.js'
import {useRoute} from 'vue-router'
import {useRouter} from 'vue-router'
import {ref, computed, onMounted, watch} from 'vue'
import {parsePhoneNumberFromString} from 'libphonenumber-js'
import {
  fetchAdminOrganisations,
  fetchOrganisations,
  fetchOrganisationRoles,
  deleteUserRole,
  createUserRole,
} from "@/api";
import EditDetailsModal from "@/components/EditAccountDetailsModal.vue";
import {getCSRFToken} from '@/store/auth'
import {API_BASE_URL} from "@/config";
import ChangePasswordModal from "@/components/ChangePasswordModal.vue";

// useRoute is used to access the current route's parameters.
// useRouter is used to navigate to the home route programmatically.

function getOrganisationsWithoutSelectedRole(organisations, userRoles) {
  const organisationsWithoutSelectedRole = [];
  organisations.forEach(organisation => {
    if (organisation.roles && organisation.roles.length > 0) {
      const hasUserRole = organisation.roles.some(role => Array.isArray(userRoles) && userRoles.some(userRole => userRole.role_id === role.id)
      );
      if (!hasUserRole) {
        organisationsWithoutSelectedRole.push(organisation);
      }
    }
  });
  return organisationsWithoutSelectedRole;
}

export default {
  methods: {createUserRole, deleteUserRole},
  components: {
    EditDetailsModal,
    ChangePasswordModal,
  },
  setup() {
    const authStore = useAuthStore()
    const route = useRoute()
    const router = useRouter()
    const user = ref({})
    const organisations = ref([])
    const adminOrganisations = ref([])
    const showEditModal = ref(false)
    const showChangePasswordModal = ref(false)
    const success = ref(route.query.success || '')
    const warning = ref(route.query.warning || '')

    const formattedMobileNumber = computed(() => {
      const mobileNumber = authStore.user?.mobile_number || ''
      const phoneNumber = parsePhoneNumberFromString(mobileNumber)
      return phoneNumber ? phoneNumber.formatInternational() : mobileNumber
    })

    const organisationsWithoutSelectedRole = ref([])

    watch(route, (newRoute) => {
      success.value = newRoute.query.success || '';
      warning.value = newRoute.query.warning || '';
    });

    onMounted(async () => {
      await authStore.fetchUser()
      user.value = authStore.user
      organisations.value = await fetchOrganisations()
      // Enrich each organisation with available roles
      for (const organisation of organisations.value) {
        organisation.roles = await fetchOrganisationRoles(organisation.slug)
      }
      adminOrganisations.value = await fetchAdminOrganisations()
      organisationsWithoutSelectedRole.value = getOrganisationsWithoutSelectedRole(organisations.value, user.value.user_roles)

    })

    function isRoleLinkedToUser(roleId) {
      // Return the user role ID if the user is linked to the role, otherwise return null
      const userRole = user.value.user_roles.find(user_role => user_role.role_id === roleId);
      return userRole ? userRole.id : null;
    }

    async function saveDetails(formData) {
      try {

        const response = await fetch(`${API_BASE_URL}/current-user/account-details`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(formData),
          credentials: 'include',
        })
        if (response.ok) {
          await authStore.fetchUser()
          user.value = authStore.user
          showEditModal.value = false
        } else {
          console.error('Failed to save user details')
        }
      } catch (error) {
        console.error('Error saving user details:', error)
      }
    }

    async function savePassword(newPassword) {
      try {
        const response = await fetch(`${API_BASE_URL}/current-user/change-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken()
          },
          body: JSON.stringify({new_password: newPassword}),
          credentials: 'include',
        });
        const data = await response.json();
        if (data.message === 'Password updated successfully') {
          showChangePasswordModal.value = false
          success.value = 'Password updated successfully'
          await router.push({path: '/account', query: {success: success.value}});
        } else {
          console.error('Failed to change password:', data)
        }
      } catch (error) {
        console.error('Error changing password:', error)
      }
    }

    async function handleCreateUserRole(userRoleId) {
      // Create the desired user role
      await createUserRole(userRoleId)
      // Update the user object with the changed list of user roles
      await authStore.fetchUser()
      user.value = authStore.user
      organisationsWithoutSelectedRole.value = getOrganisationsWithoutSelectedRole(organisations.value, user.value.user_roles)
    }

    async function handleDeleteUserRole(userRoleId) {
      // Delete the specified user role
      await deleteUserRole(userRoleId)
      // Update the user object with the changed list of user roles
      await authStore.fetchUser()
      user.value = authStore.user
      organisationsWithoutSelectedRole.value = getOrganisationsWithoutSelectedRole(organisations.value, user.value.user_roles)
    }

    async function toggleNoPreferredCoWorkers() {
      try {
        const response = await fetch(`${API_BASE_URL}/current-user/account-details`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({no_preferred_co_workers: this.user.no_preferred_co_workers}),
          credentials: 'include',
        });
        if (!response.ok) {
          console.error('Failed to update no_preferred_co_workers');
        }
      } catch (error) {
        console.error('Error updating no_preferred_co_workers:', error);
      }
    }

    return {
      authStore,
      router,
      formattedMobileNumber,
      organisations,
      adminOrganisations,
      user,
      showEditModal,
      saveDetails,
      showChangePasswordModal,
      savePassword,
      success,
      warning,
      isRoleLinkedToUser,
      handleCreateUserRole,
      handleDeleteUserRole,
      toggleNoPreferredCoWorkers,
      organisationsWithoutSelectedRole,
    }
  },

  // Below is part of the Options API
  // async mounted() {
  //   await this.authStore.fetchUser()
  // },

  computed: {
    organisationNames() {
      return this.organisations.length === 0 ? '/' : this.organisations.map(org => org.name).join(', ')
    },
    organisationAdminNames() {
      return this.adminOrganisations.map(org => org.name).join(', ')
    },
  }

}
</script>
